/**
 * Locations hub + city page copy (v4, 22 Sep 2026).
 *
 * Source: "Locations — SEO copy and build handover for Ben · v4"
 * (Website Page Specs/Locations-20260922-v4). Copy is transcribed verbatim
 * from the exact-copy appendix; apostrophes are normalised to the site's
 * straight-quote convention. Section order, CTA labels and destinations
 * are fixed by the handover — change them there first, then here.
 *
 * Claims (64% saving, 30-day contracts, 98% callbacks, 24–48h approvals,
 * 3–5 day switches, next-working-day settlement/delivery) carry their
 * qualifications inline. Customer examples are CanDo-authored text with
 * recorded naming permission for all 15 customers; they are not quotes or
 * ratings. Do not add star scores, review counts or testimonials here.
 *
 * Inline links in body/FAQ copy use `[label](/path)` and are rendered by
 * <InlineCopy /> in LocationSharedBlocks; FAQ schema receives the plain
 * text so JSON-LD matches the visible answer word for word.
 */

export const CTA = {
  statement: { label: "Get a free statement review", to: "/statement-review" },
  inPerson: { label: "Estimate in-person payment costs", to: "/quote/in-person" },
  online: { label: "Taking payments online? Estimate online costs", to: "/quote/online" },
};

export const PROOF_STRIP = {
  items: [
    "Save up to 64% on payment processing",
    "Terminal contracts from 30 days",
    "98% of callbacks returned within two business hours",
  ],
  note: "Savings depend on your current setup and eligibility. Contract options run from 30 days to 18 months.",
};

export const CHOICE_BLOCK = {
  eyebrow: "Start in the right place",
  heading: "Already taking payments, or starting from scratch?",
  existing: {
    heading: "Already taking card payments?",
    body:
      "Have a recent merchant statement ready. We'll check your rates, contract and settlement details, explain what you're paying, and show you what could change. Where eligible, we'll match or beat your current payment rate.",
    cta: CTA.statement,
    support: "The form also asks for your business and contact details.",
  },
  newBusiness: {
    heading: "Setting up a new business?",
    body:
      "You do not need a merchant statement. Start with an estimate for the way you expect customers to pay, then ask us for a quote tailored to your business.",
    cta: CTA.inPerson,
    link: CTA.online,
    support: "Estimates are indicative, not formal quotes.",
  },
};

export const SOLUTIONS_BLOCK = {
  eyebrow: "Ways to take payment",
  headingFor: (location) => `Choose what works for your ${location} business`,
  intro: "Start with what you need now. You can add other ways to take payment as your business changes.",
  items: [
    { heading: "Card machines", body: "Countertop, portable and mobile terminals for taking payments face to face.", to: "/card-machines" },
    { heading: "EPOS systems", body: "Tills, ordering and reporting tools for hospitality, retail and other customer-facing businesses.", to: "/epos-systems" },
    { heading: "Online payments", body: "Take payments through your website or online checkout, with rates from 1% + 15p.", to: "/online-payments" },
    { heading: "Phone payments", body: "Take remote payments using a virtual terminal.", to: "/virtual-terminal" },
    { heading: "Online ordering", body: "Give customers a simple way to order and pay online.", to: "/online-ordering" },
    { heading: "Business funding", body: "Merchant cash advance options linked to card takings, subject to eligibility.", to: "/merchant-cash-advance" },
  ],
};

export const SUPPORT_BLOCK = {
  eyebrow: "Local support",
  heading: "People you can actually get hold of",
  body:
    "We're based in Ellesmere Port. You'll have a named account manager, and we can arrange local site visits at no extra cost when they're useful. If you need help, you'll speak to people who understand your account and how your business takes payments.",
};

export const REVIEW_BLOCK = {
  eyebrow: "Customer stories",
  heading: "Do not just take our word for it",
  body: "See what business owners say about working with CanDo.",
  cta: { label: "Read customer reviews", to: "/reviews" },
};

export const FINAL_CTA = {
  heading: "Ready to compare your payment options?",
  body:
    "Already taking payments? Start with a free statement review. Setting up a new business? Explore in-person or online payment costs, then ask us for a quote tailored to your business.",
  primary: CTA.statement,
  secondary: CTA.inPerson,
  link: CTA.online,
};

export const HUB = {
  path: "/locations",
  // Service.areaServed for the hub: every area with its own page, plus the
  // North West counties they sit in.
  areaServed: [
    { "@type": "City", name: "Liverpool" },
    { "@type": "City", name: "Manchester" },
    { "@type": "City", name: "Chester" },
    { "@type": "City", name: "Warrington" },
    { "@type": "AdministrativeArea", name: "Wirral" },
    { "@type": "AdministrativeArea", name: "Cheshire" },
    { "@type": "AdministrativeArea", name: "Merseyside" },
    { "@type": "AdministrativeArea", name: "Greater Manchester" },
  ],
  seoTitle: "Card Machines & Payment Support in the North West | CanDo",
  metaDescription:
    "Card machines and payment support across Liverpool, Manchester, Chester, Warrington and the Wirral. Compare providers or explore a new-business setup.",
  eyebrow: "Local payment support",
  h1: "Card machines and local payment support across the North West",
  heroBody:
    "We're based in Ellesmere Port and help businesses across Liverpool, Manchester, Chester, Warrington, the Wirral and surrounding areas take payments in person, online and over the phone. Choose your area below, or start with a statement review or payment estimate.",
  finder: {
    eyebrow: "Find your nearest page",
    heading: "Where do you do business?",
    intro:
      "Choose the nearest area for local information and useful links. If your town is not listed, ask us—we also support businesses in surrounding areas.",
    areas: [
      { slug: "liverpool", label: "Liverpool", body: "Card machines, handheld EPOS and payment links for venues, hospitality and accommodation." },
      { slug: "manchester", label: "Manchester", body: "Card machines, ecommerce payments and EPOS for retail, wholesale and leisure businesses." },
      { slug: "chester", label: "Chester", body: "Card machines, iPad EPOS and payment links, with support from our nearby Ellesmere Port team." },
      { slug: "warrington", label: "Warrington", body: "Online, telephone and card payments for businesses across Warrington, Lymm and nearby areas." },
      { slug: "wirral", label: "Wirral", body: "EPOS, subscriptions and invoice payments for gyms, leisure and professional services." },
    ],
  },
  notListed: {
    heading: "Not listed? That does not mean we cannot help.",
    body:
      "These pages cover the areas closest to our Ellesmere Port base. Tell us where you trade and how customers pay, and we'll let you know what support is available.",
    cta: { label: "Talk to CanDo", to: "/book" },
  },
  faqs: [
    {
      q: "Which areas do you cover?",
      a: "We support businesses across Liverpool, Manchester, Chester, Warrington, the Wirral and surrounding areas. If your town is not listed, contact us and we'll confirm whether we can help.",
    },
    {
      q: "Can you check the deal I have with my current provider?",
      a: "Yes. Send us a recent merchant statement and we'll check the rates, contract costs and settlement details. We'll explain what you're paying in plain English and show you what could change.",
    },
    {
      q: "How do I compare payment costs for a new business?",
      a: "You do not need an existing merchant statement. Start with an [in-person payment estimate](/quote/in-person) or an [online payment estimate](/quote/online), then ask us for a tailored quote. The estimates are indicative, not formal quotes.",
    },
    {
      q: "How long does it take to get set up or switch?",
      a: "Merchant approvals can be completed in 24–48 hours, gateways can be live within 24 hours, and provider switches can be completed in 3–5 working days. The exact timing depends on approval, the information required and your existing setup.",
    },
    {
      q: "When will card payments reach my account?",
      a: "Next-working-day settlement is available as standard. Same-day funding is available in some circumstances, subject to eligibility and applicable cut-off times.",
    },
    {
      q: "How quickly can I receive a card machine?",
      a: "Order before 2pm for next-working-day terminal delivery, subject to stock and delivery area.",
    },
  ],
};

export const LOCATIONS = {
  liverpool: {
    slug: "liverpool",
    // Service.areaServed for this page: the city/borough and its county.
    areaServed: [{ "@type": "City", name: "Liverpool" }, { "@type": "AdministrativeArea", name: "Merseyside" }],
    title: "Liverpool",
    // Used in shared headings ("your {name} business", breadcrumbs).
    name: "Liverpool",
    seoTitle: "Card Machines in Liverpool | Local Payment Support | CanDo",
    metaDescription:
      "Card machines, handheld EPOS and online payments for Liverpool businesses. Local support for venues, hospitality and accommodation. Compare your costs.",
    eyebrow: "Payment support in Liverpool",
    h1: "Card machines and payment support in Liverpool",
    heroBody:
      "From busy venues to aparthotels, we help Liverpool businesses take card payments at the counter, at the table and online. Choose card machines, handheld EPOS and payment links to suit how you trade, with support from our Ellesmere Port team.",
    situations: {
      eyebrow: "How we can help",
      heading: "Payments that keep up with the way you trade",
      items: [
        { heading: "Busy counters and tables", body: "Match the right card machines and EPOS setup to the way your team serves customers." },
        { heading: "In-person and online sales", body: "Bring different ways to pay together instead of managing disconnected systems." },
        { heading: "A deal worth checking", body: "Let us review your statement, contract and settlement setup before you decide whether to switch." },
      ],
    },
    customers: {
      eyebrow: "Local customers",
      heading: "Card payments and EPOS for Liverpool businesses",
      items: [
        {
          name: "Invisible Wind Factory",
          body: "We supplied Invisible Wind Factory with card payments and [handheld EPOS](/epos-systems), helping the team shorten service times and serve customers more quickly.",
        },
        {
          name: "Sevenstay Aparthotels",
          body: "We helped Sevenstay Aparthotels take accommodation payments directly through [payment links](/online-payments) and pre-authorisations, giving the team an alternative to processing payments through a booking platform.",
        },
        {
          name: "Cavern Club",
          body: "We support Cavern Club with card machines, handheld EPOS and online payments, covering both in-person and online transactions.",
        },
      ],
    },
    faqs: [
      {
        q: "Do you cover businesses outside Liverpool city centre?",
        a: "Yes. We support businesses across Liverpool, wider Merseyside and surrounding areas. Tell us where you trade when you enquire.",
      },
      {
        q: "Can you help Liverpool venues and accommodation businesses take payments?",
        a: "Yes. We supply card machines, handheld EPOS, online payments and payment links. Our local work includes Invisible Wind Factory, Cavern Club and Sevenstay Aparthotels. Tell us whether you need payments at the counter, at the table or before a guest arrives.",
      },
      {
        q: "Can you review my current Liverpool payment provider?",
        a: "Yes. Send us a recent merchant statement and we'll explain the rates, contract and settlement details before discussing alternatives.",
      },
      {
        q: "When will my card payments settle?",
        a: "Next-working-day settlement is available as standard. Same-day funding may be available in some circumstances, subject to eligibility and cut-off times.",
      },
    ],
    nearby: [
      { label: "Card machines in Manchester", to: "/locations/manchester" },
      { label: "Payment support in Warrington", to: "/locations/warrington" },
      { label: "Card machines in the Wirral", to: "/locations/wirral" },
    ],
  },

  manchester: {
    slug: "manchester",
    // Service.areaServed for this page: the city/borough and its county.
    areaServed: [{ "@type": "City", name: "Manchester" }, { "@type": "AdministrativeArea", name: "Greater Manchester" }],
    title: "Manchester",
    name: "Manchester",
    seoTitle: "Card Machines in Manchester | Local Payment Support | CanDo",
    metaDescription:
      "Card machines, EPOS and ecommerce payments for Manchester retailers, wholesalers and leisure businesses. Local support and a review of your payment costs.",
    eyebrow: "Payment support in Manchester",
    h1: "Card machines and payment support in Manchester",
    heroBody:
      "Selling from a shop, supplying trade customers or running an online checkout? We help businesses across Manchester and Greater Manchester choose card machines, EPOS and ecommerce payments, with support that continues after setup.",
    situations: {
      eyebrow: "How we can help",
      heading: "One payment setup, however your business sells",
      items: [
        { heading: "More than one site or sales channel", body: "Compare ways to take payments across counters, venues, websites and phone orders." },
        { heading: "Opening or switching on a deadline", body: "Merchant approvals can be completed in 24–48 hours, gateways can be live within 24 hours, and provider switches can be completed in 3–5 working days." },
        { heading: "Support when trading is busy", body: "Speak to people who understand your account instead of starting again with a call-centre script." },
      ],
      note: "Setup and switching times depend on approval, the information required and your existing setup.",
    },
    customers: {
      eyebrow: "Local customers",
      heading: "Card machines and online payments for Manchester businesses",
      note: "These examples include businesses in Manchester and the surrounding area; Velocity Padel operates in Wigan and Manchester.",
      items: [
        {
          name: "Kurt Muller",
          body: "We helped national wholesale retailer Kurt Muller reduce its payment-processing costs with card payments and payment links. After a short time with CanDo, we helped secure further savings, continuing our support beyond the initial setup.",
        },
        {
          name: "The 1755 Collection",
          body: "We helped antiques retailer The 1755 Collection take payments in-store and online, with card payments, EPOS and ecommerce payments on its website.",
        },
        {
          name: "AD Teamwear",
          body: "We moved AD Teamwear's WooCommerce payments from Stripe to the [CanDo payment gateway](/online-payments), reducing card-processing costs and arranging next-day settlement for the business.",
        },
        {
          name: "Velocity Padel",
          body: "We support Velocity Padel's clubs in Wigan and Manchester with integrated EPOS and card payments, covering retail sales, court bookings and online bookings.",
        },
      ],
    },
    faqs: [
      {
        q: "Do you cover businesses across Greater Manchester?",
        a: "Yes. We support businesses across Manchester, Greater Manchester and surrounding areas. Tell us where you trade when you enquire.",
      },
      {
        q: "Can you help a Manchester business switch its WooCommerce payment gateway?",
        a: "Yes. We moved AD Teamwear's WooCommerce payments from Stripe to the CanDo gateway. We can review your checkout, payment costs and settlement requirements before recommending a change. Compatibility and terms depend on your setup.",
      },
      {
        q: "How quickly can I change payment provider?",
        a: "Provider switches can be completed in 3–5 working days. The exact timing depends on approval, the information available and your current setup.",
      },
      {
        q: "Can you help a new Manchester business?",
        a: "Yes. You do not need a merchant statement. Start with an [in-person payment estimate](/quote/in-person) or an [online payment estimate](/quote/online), depending on how you expect customers to pay. You can then ask us for a tailored quote; the estimate itself is not a formal quote.",
      },
    ],
    nearby: [
      { label: "Card machines in Liverpool", to: "/locations/liverpool" },
      { label: "Payment support in Warrington", to: "/locations/warrington" },
      { label: "Card machines in Chester", to: "/locations/chester" },
    ],
  },

  chester: {
    slug: "chester",
    // Service.areaServed for this page: the city/borough and its county.
    areaServed: [{ "@type": "City", name: "Chester" }, { "@type": "AdministrativeArea", name: "Cheshire" }],
    title: "Chester",
    name: "Chester",
    seoTitle: "Card Machines in Chester | Local Payment Support | CanDo",
    metaDescription:
      "Card machines, iPad EPOS and payment links for Chester hospitality and property businesses. Local support from Ellesmere Port. Review your payment costs.",
    eyebrow: "Payment support in Chester",
    h1: "Card machines and payment support in Chester",
    heroBody:
      "Taking restaurant payments, arranging wedding deposits or sending payment links from an estate agency? We help Chester businesses choose card machines, EPOS and online payments, with local support from our nearby Ellesmere Port team.",
    situations: {
      eyebrow: "How we can help",
      heading: "Local support when a phone call is not enough",
      items: [
        { heading: "A team based nearby", body: "We can arrange local site visits at no extra cost and same-morning visits in Chester when they are needed and available." },
        { heading: "Flexible terminal terms", body: "Choose from contract options between 30 days and 18 months, without five- or six-year terminal-rental agreements or automatic renewal." },
        { heading: "A proper comparison", body: "Send us your statement and we'll explain the deal you have now. Where eligible, we'll match or beat your current payment rate." },
      ],
    },
    customers: {
      eyebrow: "Local customers",
      heading: "Payment support for Chester hospitality and property businesses",
      items: [
        {
          name: "The Coachhouse",
          body: "We supplied The Coachhouse with [iPad-based EPOS](/epos-systems), card machines and online payments. Payment links also make it easier for the team to arrange wedding deposits.",
        },
        {
          name: "Porta Tapas",
          body: "We support Porta Tapas and related sites with card payments and local support for their teams. Bill-splitting software on the terminals helps staff handle customers who want to pay separately.",
        },
        {
          name: "Currans estate agents",
          body: "We helped Currans estate agents upgrade their payment-link system, with support focused on the security of their card-payment process.",
        },
      ],
    },
    faqs: [
      {
        q: "Is CanDo based near Chester?",
        a: "Yes. We're based in Ellesmere Port and support businesses across Chester and surrounding areas.",
      },
      {
        q: "Can you visit my Chester business?",
        a: "Yes. We offer local site visits at no extra cost and can arrange same-morning visits in Chester when they are needed and available.",
      },
      {
        q: "Do I need to sign a long terminal contract?",
        a: "No. Our terminal contract options run from 30 days to 18 months. We do not use five- or six-year terminal-rental agreements or automatic renewal.",
      },
      {
        q: "Can I use payment links for wedding deposits or other remote payments?",
        a: "Yes. We helped The Coachhouse collect wedding deposits through payment links and supported Currans estate agents with its payment-link system. Tell us what you need to collect and we'll discuss a suitable payment process.",
      },
    ],
    nearby: [
      { label: "Card machines in the Wirral", to: "/locations/wirral" },
      { label: "Card machines in Liverpool", to: "/locations/liverpool" },
      { label: "Payment support in Warrington", to: "/locations/warrington" },
    ],
  },

  warrington: {
    slug: "warrington",
    // Service.areaServed for this page: the city/borough and its county.
    areaServed: [{ "@type": "City", name: "Warrington" }, { "@type": "AdministrativeArea", name: "Cheshire" }],
    title: "Warrington",
    name: "Warrington",
    seoTitle: "Card Machines in Warrington | Local Payment Support | CanDo",
    metaDescription:
      "Card machines, OpenCart payments, payment links and phone payments for Warrington businesses. Practical setup help and ongoing local support from CanDo.",
    eyebrow: "Payment support in Warrington",
    h1: "Card machines and payment support in Warrington",
    heroBody:
      "Taking trade orders, accepting payments through a website or sending a customer a payment link? We help businesses across Warrington, Lymm and surrounding areas with card machines, online and telephone payments, plus practical help getting started.",
    situations: {
      eyebrow: "How we can help",
      heading: "Payment options for the counter, the office and online",
      items: [
        { heading: "Counter and face-to-face sales", body: "Compare countertop, portable and mobile terminals for the way your team serves customers." },
        { heading: "Phone and online payments", body: "Take remote payments through a virtual terminal or add online payments to your website." },
        { heading: "One person to call", body: "You'll have a named account manager, with 98% of callbacks returned within two business hours." },
      ],
    },
    customers: {
      eyebrow: "Local customers",
      heading: "Online and card payments for Warrington-area businesses",
      note: "These examples include businesses in Warrington and the surrounding area, including Lymm.",
      items: [
        {
          name: "Lymm Engine Components",
          body: "We integrated a [payment gateway](/online-payments) with Lymm Engine Components' OpenCart website and supported payment links and telephone payments. The setup reduced processing costs and improved payment-processing efficiency, with funds reaching the bank the next day.",
        },
        {
          name: "Steaua Nordului",
          body: "We helped recruitment company Steaua Nordului start accepting card payments through payment links. After training from our team, payment links became an established part of the way the business takes payments.",
        },
      ],
    },
    faqs: [
      {
        q: "Do you support trade and business-to-business payments in Warrington?",
        a: "Yes. We can discuss in-person, online and phone payment requirements with trade counters, wholesalers, professional services and other suitable businesses.",
      },
      {
        q: "Can you help with an OpenCart payment gateway?",
        a: "Yes. Our work with Lymm Engine Components includes an OpenCart payment gateway, payment links and telephone payments. We'll review your website and payment requirements before confirming the right integration for your business.",
      },
      {
        q: "Who do I contact after I become a customer?",
        a: "You'll have a named account manager who understands your account and payment setup.",
      },
      {
        q: "Can you review my current provider?",
        a: "Yes. Send us a recent merchant statement and we'll explain your current rates, contract and settlement details before discussing alternatives.",
      },
    ],
    nearby: [
      { label: "Card machines in Manchester", to: "/locations/manchester" },
      { label: "Card machines in Liverpool", to: "/locations/liverpool" },
      { label: "Card machines in Chester", to: "/locations/chester" },
    ],
  },

  wirral: {
    slug: "wirral",
    // Service.areaServed for this page: the city/borough and its county.
    areaServed: [{ "@type": "AdministrativeArea", name: "Wirral" }, { "@type": "AdministrativeArea", name: "Merseyside" }],
    title: "Wirral",
    name: "Wirral",
    seoTitle: "Card Machines in Wirral | Local Payment Support | CanDo",
    metaDescription:
      "Card machines, EPOS, subscriptions and invoice payments for Wirral businesses. Local support for gyms, sports bars and professional services from CanDo.",
    eyebrow: "Payment support in the Wirral",
    h1: "Card machines and payment support in the Wirral",
    heroBody:
      "From gym subscriptions and sports-bar bookings to solicitor invoice payments, Wirral businesses need different ways to take payment. We help you choose card machines, EPOS and online payment options, with support from our nearby Ellesmere Port team.",
    situations: {
      eyebrow: "How we can help",
      heading: "Payments that can move with your business",
      items: [
        { heading: "Fixed, portable or mobile", body: "Choose a terminal that fits where customers pay instead of working around the wrong machine." },
        { heading: "Starting or replacing equipment", body: "Order before 2pm for next-working-day terminal delivery, subject to stock and delivery area." },
        { heading: "Support from nearby", body: "Our Ellesmere Port team can arrange local site visits at no extra cost when they are useful." },
      ],
    },
    customers: {
      eyebrow: "Local customers",
      heading: "Payment systems for Wirral gyms, leisure and professional services",
      items: [
        {
          name: "Easthams Solicitors",
          body: "We helped Easthams Solicitors offer [online invoice payments](/online-payments) through payment links and a dedicated page on their website, with a payment process tailored to their accounts team's review.",
        },
        {
          name: "Apex Wirral",
          body: "We support local private gym Apex Wirral with EPOS and subscription payments as part of its class-management setup.",
        },
        {
          name: "Breakers Sports Bar",
          body: "We supplied Breakers Sports Bar with an integrated EPOS system, online booking and member management for its pool and sports bar operation.",
        },
      ],
    },
    faqs: [
      {
        q: "Which parts of the Wirral do you cover?",
        a: "We support businesses across Birkenhead, Bromborough, Heswall, Hoylake, West Kirby, New Brighton and surrounding areas. Contact us if your town is not listed.",
      },
      {
        q: "Can you help a Wirral gym or sports bar with payments and bookings?",
        a: "Yes. Apex Wirral uses EPOS and subscription payments as part of its class-management setup. Breakers Sports Bar uses integrated EPOS, online booking and member management. We'll discuss the features and connections your business needs before recommending a system.",
      },
      {
        q: "Can I choose a shorter terminal contract?",
        a: "Yes. Our terminal contract options run from 30 days to 18 months, without five- or six-year terminal-rental agreements or automatic renewal.",
      },
      {
        q: "Can you help if my business is seasonal or mobile?",
        a: "Yes. Tell us where customers pay and how trading changes through the year. We'll help you compare fixed, portable, mobile and online options.",
      },
    ],
    nearby: [
      { label: "Card machines in Liverpool", to: "/locations/liverpool" },
      { label: "Card machines in Chester", to: "/locations/chester" },
      { label: "Payment support in Warrington", to: "/locations/warrington" },
    ],
  },
};

export const LOCATION_SLUGS = Object.keys(LOCATIONS);

/** Strip `[label](/path)` markup so FAQ JSON-LD text matches the visible answer. */
export function plainText(copy) {
  return String(copy || "").replace(/\[([^\]]+)\]\([^)]+\)/g, "$1");
}
